<script setup lang="ts">
  import { ref } from "vue";
  import { Upload, Loader2, X } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import useMyFetch from "@stores/useMyFetch";
  import { useToastsStore } from "@stores/toastsStore";
  import { matchesAccept } from "@utils/fileAccept";

  const props = withDefaults(
    defineProps<{
      label: string;
      accept?: string;
    }>(),
    { accept: "image/*" },
  );

  const toasts = useToastsStore();

  const urls = defineModel<string[]>({ required: true });

  const isUploading = ref(false);
  const replacingIndex = ref<number | null>(null);
  const fileInput = ref<HTMLInputElement | null>(null);

  const openPicker = (index: number | null = null) => {
    replacingIndex.value = index;
    fileInput.value?.click();
  };

  const handleFileSelect = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files || []);
    if (!files.length) return;

    isUploading.value = true;
    try {
      for (const file of files) {
        if (!matchesAccept(file, props.accept)) {
          toasts.error("Format non pris en charge", file.name);
          continue;
        }
        const formData = new FormData();
        formData.append("file[attachment]", file);
        formData.append("file[type]", file.type);
        const { data, error } = await useMyFetch("/file_uploads/").post(formData, null).json<{ url: string }>();
        if (error.value) throw error.value;
        if (replacingIndex.value != null) {
          urls.value = urls.value.map((url, i) => (i === replacingIndex.value ? data.value!.url : url));
          break;
        }
        urls.value = [...urls.value, data.value!.url];
      }
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      isUploading.value = false;
      replacingIndex.value = null;
      target.value = "";
    }
  };

  const removeImage = (index: number) => {
    urls.value = urls.value.filter((_, i) => i !== index);
  };
</script>

<template>
  <div class="space-y-2">
    <Label class="text-sm">{{ label }}</Label>
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      :multiple="replacingIndex == null"
      class="hidden"
      @change="handleFileSelect" />
    <div class="flex flex-wrap gap-3">
      <div
        v-for="(url, index) in urls"
        :key="url"
        class="group relative">
        <img
          :src="url"
          class="h-20 w-20 cursor-pointer rounded-md border object-cover"
          @click="openPicker(index)" />
        <button
          type="button"
          class="bg-destructive text-destructive-foreground absolute -right-2 -top-2 rounded-full p-0.5 opacity-0 transition-opacity group-hover:opacity-100"
          @click="removeImage(index)">
          <X class="h-3 w-3" />
        </button>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        class="self-center text-sm"
        :disabled="isUploading"
        @click="openPicker()">
        <Loader2
          v-if="isUploading"
          class="mr-2 h-4 w-4 animate-spin" />
        <Upload
          v-else
          class="mr-2 h-4 w-4" />
        {{ isUploading ? "Envoi…" : "Ajouter une image" }}
      </Button>
    </div>
  </div>
</template>
